import { el } from './dom';
import { promptText } from './platform-interactions';

export interface MergeFieldDto {
    path: string;
    displayName: string;
}

export interface MergeFieldGroupDto {
    title: string;
    fields: MergeFieldDto[];
}

export interface RichTextOptions {
    /** Plain-text mode (headings): no formatting, commits textContent. */
    plain?: boolean;
    /** `floating` = template editor popover; `inline` = persistent bar above editor (forms). */
    toolbarMode?: 'floating' | 'inline';
    /** When provided, the toolbar shows a merge-field picker (template editor). */
    getMergeGroups?: () => MergeFieldGroupDto[];
    /** Commit handler — receives innerHTML (rich) or textContent (plain). */
    onInput: (value: string) => void;
    /** Fired when the user presses Escape or blurs. */
    onExit?: () => void;
}

let floatingToolbar: HTMLElement | null = null;
let activeNode: HTMLElement | null = null;
let activeOptions: RichTextOptions | null = null;
let hideTimer: number | null = null;

export function wireRichText(node: HTMLElement, opts: RichTextOptions): void {
    if (node.dataset.rtWired === '1') return;
    node.dataset.rtWired = '1';
    node.setAttribute('contenteditable', opts.plain ? 'plaintext-only' : 'true');
    node.spellcheck = true;
    node.classList.add('sp-richtext-editor');
    if (node.classList.contains('tpl-rt') === false && node.closest('.tpl-canvas-host, .tpl-root, #tpl-root')) {
        node.classList.add('tpl-rt');
    }

    const toolbarMode = opts.toolbarMode ?? 'floating';
    let inlineBar: HTMLElement | null = null;
    if (!opts.plain && toolbarMode === 'inline') {
        const visual = node.closest('.sp-richtext-visual');
        if (visual && !visual.querySelector('.sp-richtext-toolbar--inline')) {
            inlineBar = buildToolbarBar(!!opts.getMergeGroups, true, node, opts);
            visual.insertBefore(inlineBar, visual.firstChild);
        }
    }

    const commit = () => {
        opts.onInput(opts.plain ? (node.textContent ?? '') : node.innerHTML);
    };

    const commitFromNode = () => commit();

    node.addEventListener('input', commitFromNode);
    node.addEventListener('mousedown', (ev) => ev.stopPropagation());
    if (toolbarMode === 'floating' && !opts.plain) {
        node.addEventListener('focus', () => {
            activeNode = node;
            activeOptions = opts;
            showFloatingToolbar(node, !!opts.getMergeGroups);
        });
        node.addEventListener('blur', () => {
            commit();
            scheduleHideFloating();
            opts.onExit?.();
        });
    } else {
        node.addEventListener('blur', () => {
            commit();
            opts.onExit?.();
        });
    }
    node.addEventListener('keydown', (ev) => {
        if (ev.key === 'Escape') { ev.preventDefault(); node.blur(); }
        if (opts.plain && ev.key === 'Enter') { ev.preventDefault(); node.blur(); }
    });

    if (inlineBar) {
        inlineBar.querySelectorAll<HTMLButtonElement>('.sp-richtext-toolbar__btn').forEach((btn) => {
            btn.addEventListener('mousedown', (ev) => ev.preventDefault());
        });
    }
}

function buildToolbarBar(includeMerge: boolean, inline: boolean, editorNode?: HTMLElement, editorOpts?: RichTextOptions): HTMLElement {
    const bar = el('div', {
        class: inline
            ? 'sp-richtext-toolbar sp-richtext-toolbar--inline tpl-rt-toolbar'
            : 'sp-richtext-toolbar tpl-rt-toolbar',
        role: 'toolbar',
    });
    bar.addEventListener('mousedown', (ev) => ev.preventDefault());

    const run = (fn: () => void) => {
        if (editorNode && editorOpts) {
            activeNode = editorNode;
            activeOptions = editorOpts;
            editorNode.focus();
        }
        fn();
    };

    bar.appendChild(cmdButton('bi-type-bold', 'Bold', () => run(() => exec('bold'))));
    bar.appendChild(cmdButton('bi-type-italic', 'Italic', () => run(() => exec('italic'))));
    bar.appendChild(cmdButton('bi-type-underline', 'Underline', () => run(() => exec('underline'))));
    bar.appendChild(sep());
    bar.appendChild(cmdButton('bi-list-ul', 'Bullet list', () => run(() => exec('insertUnorderedList'))));
    bar.appendChild(cmdButton('bi-list-ol', 'Numbered list', () => run(() => exec('insertOrderedList'))));
    bar.appendChild(sep());
    bar.appendChild(cmdButton('bi-text-left', 'Align left', () => run(() => exec('justifyLeft'))));
    bar.appendChild(cmdButton('bi-text-center', 'Align centre', () => run(() => exec('justifyCenter'))));
    bar.appendChild(cmdButton('bi-text-right', 'Align right', () => run(() => exec('justifyRight'))));
    bar.appendChild(sep());
    bar.appendChild(cmdButton('bi-link-45deg', 'Insert link', () => run(insertLink)));
    bar.appendChild(cmdButton('bi-eraser', 'Clear formatting', () => run(() => exec('removeFormat'))));
    bar.appendChild(sep());
    bar.appendChild(mergeButton(bar, includeMerge));

    return bar;
}

function ensureFloatingToolbar(includeMerge: boolean): HTMLElement {
    if (floatingToolbar) {
        const mergeWrap = floatingToolbar.querySelector('.sp-richtext-toolbar__merge');
        if (mergeWrap) mergeWrap.classList.toggle('d-none', !includeMerge);
        return floatingToolbar;
    }

    const bar = buildToolbarBar(includeMerge, false);
    document.body.appendChild(bar);
    floatingToolbar = bar;
    return bar;
}

function showFloatingToolbar(node: HTMLElement, includeMerge: boolean): void {
    const bar = ensureFloatingToolbar(includeMerge);
    if (hideTimer) { window.clearTimeout(hideTimer); hideTimer = null; }
    bar.style.display = 'flex';
    const rect = node.getBoundingClientRect();
    const top = Math.max(8, rect.top - bar.offsetHeight - 8);
    bar.style.top = `${top}px`;
    bar.style.left = `${Math.max(8, rect.left)}px`;
}

function scheduleHideFloating(): void {
    if (hideTimer) window.clearTimeout(hideTimer);
    hideTimer = window.setTimeout(() => {
        if (floatingToolbar) floatingToolbar.style.display = 'none';
        activeNode = null;
        activeOptions = null;
    }, 200);
}

function exec(command: string, value?: string): void {
    if (activeNode) activeNode.focus();
    document.execCommand(command, false, value);
    commitActive();
}

function commitActive(): void {
    if (activeNode && activeOptions) {
        activeOptions.onInput(activeOptions.plain ? (activeNode.textContent ?? '') : activeNode.innerHTML);
    } else {
        const sel = window.getSelection();
        const anchor = sel?.anchorNode;
        const editor = anchor instanceof HTMLElement
            ? anchor.closest<HTMLElement>('.sp-richtext-editor[data-rt-wired="1"]')
            : anchor?.parentElement?.closest<HTMLElement>('.sp-richtext-editor[data-rt-wired="1"]');
        if (editor) {
            const hidden = editor.closest('.sp-richtext-field')?.querySelector<HTMLInputElement>('.sp-richtext-value');
            if (hidden) {
                hidden.value = editor.innerHTML;
                hidden.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
    }
}

function captureActiveSelection(): Range | null {
    if (!activeNode) return null;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0);
    if (!activeNode.contains(range.commonAncestorContainer)) return null;
    return range.cloneRange();
}

function restoreActiveSelection(range: Range | null): void {
    if (!activeNode || !range) return;
    activeNode.focus();
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
}

async function insertLink(): Promise<void> {
    const selection = captureActiveSelection();
    const url = await promptText({
        title: 'Insert link',
        message: 'Enter the destination for the selected text.',
        inputLabel: 'Link URL',
        value: 'https://',
        inputType: 'url',
        confirmLabel: 'Insert link',
        cancelLabel: 'Cancel',
        icon: 'bi-link-45deg',
    });
    restoreActiveSelection(selection);
    if (!url?.trim()) return;
    exec('createLink', url.trim());
}

function cmdButton(icon: string, title: string, onClick: () => void): HTMLElement {
    const b = el('button', {
        class: 'sp-richtext-toolbar__btn tpl-rt-toolbar__btn',
        type: 'button',
        title,
        'aria-label': title,
    });
    b.appendChild(el('i', { class: `bi ${icon}` }));
    b.addEventListener('click', (ev) => { ev.preventDefault(); onClick(); });
    return b;
}

function sep(): HTMLElement { return el('span', { class: 'sp-richtext-toolbar__sep tpl-rt-toolbar__sep' }); }

function mergeButton(bar: HTMLElement, includeMerge: boolean): HTMLElement {
    const wrap = el('div', { class: 'sp-richtext-toolbar__merge tpl-rt-toolbar__merge' + (includeMerge ? '' : ' d-none') });
    const b = el('button', {
        class: 'sp-richtext-toolbar__btn tpl-rt-toolbar__btn',
        type: 'button',
        title: 'Insert merge field',
        'aria-label': 'Insert merge field',
    });
    b.appendChild(el('i', { class: 'bi bi-braces' }));
    const panel = el('div', { class: 'sp-richtext-toolbar__merge-panel tpl-rt-toolbar__merge-panel', hidden: 'hidden' });

    b.addEventListener('click', (ev) => {
        ev.preventDefault();
        if (!panel.hasAttribute('hidden')) { panel.setAttribute('hidden', 'hidden'); return; }
        buildMergePanel(panel);
        panel.removeAttribute('hidden');
    });

    wrap.appendChild(b);
    wrap.appendChild(panel);
    void bar;
    return wrap;
}

function buildMergePanel(panel: HTMLElement): void {
    panel.innerHTML = '';
    const groups = activeOptions?.getMergeGroups?.() ?? [];
    const search = el('input', {
        class: 'form-control form-control-sm sp-richtext-toolbar__search tpl-rt-toolbar__search',
        placeholder: 'Search fields…',
        'aria-label': 'Search merge fields',
    }) as HTMLInputElement;
    panel.appendChild(search);
    // Campaign emails need a fallback for people whose record has no value; it is written as | default('…').
    const fallback = el('input', {
        class: 'sp-richtext-toolbar__search tpl-rt-toolbar__search',
        placeholder: 'Fallback when empty, e.g. there',
        'aria-label': 'Fallback when the field is empty',
    }) as HTMLInputElement;
    fallback.addEventListener('mousedown', (ev) => ev.stopPropagation());
    panel.appendChild(fallback);
    const list = el('div', { class: 'sp-richtext-toolbar__merge-list tpl-rt-toolbar__merge-list' });
    panel.appendChild(list);

    const render = (filter: string) => {
        list.innerHTML = '';
        const q = filter.trim().toLowerCase();
        for (const g of groups) {
            const matches = g.fields.filter(f =>
                !q || f.displayName.toLowerCase().includes(q) || f.path.toLowerCase().includes(q));
            if (matches.length === 0) continue;
            list.appendChild(el('div', { class: 'sp-richtext-toolbar__merge-group tpl-rt-toolbar__merge-group' }, g.title));
            for (const f of matches) {
                const item = el('button', { class: 'sp-richtext-toolbar__merge-item tpl-rt-toolbar__merge-item', type: 'button' });
                item.appendChild(el('span', { class: 'sp-richtext-toolbar__merge-name tpl-rt-toolbar__merge-name' }, f.displayName));
                item.appendChild(el('code', {}, `{{ ${f.path} }}`));
                item.addEventListener('mousedown', (ev) => ev.preventDefault());
                item.addEventListener('click', (ev) => {
                    ev.preventDefault();
                    insertMergeToken(f.path, fallback.value);
                    panel.setAttribute('hidden', 'hidden');
                });
                list.appendChild(item);
            }
        }
        if (!list.firstChild) list.appendChild(el('div', { class: 'sp-richtext-toolbar__merge-empty tpl-rt-toolbar__merge-empty' }, 'No matching fields'));
    };

    search.addEventListener('input', () => render(search.value));
    render('');
}

function insertMergeToken(path: string, fallback = ''): void {
    if (!activeNode) return;
    activeNode.focus();
    const text = fallback.replace(/[{}|]/g, '').trim();
    const quote = text.includes("'") ? '"' : "'";
    const expression = text ? `${path} | default(${quote}${text.replace(/["']/g, quote === '"' ? "'" : '"')}${quote})` : path;
    document.execCommand('insertText', false, `{{ ${expression} }}`);
    commitActive();
}
